/**
 * Transactions Page Component
 * 
 * Main page for viewing and managing transactions
 * 
 * Features:
 * - Transaction list with filtering and sorting
 * - Import transactions from CSV
 * - Edit, split, tag, and delete transactions
 * - Bulk operations
 * - WCAG 2.1 AA compliance
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { TransactionList } from '../components/transactions/TransactionList';
import { ImportModal } from '../components/transactions/ImportModal';
import { TransactionEditModal } from '../components/transactions/TransactionEditModal';
import { TransactionCategoriseModal } from '../components/transactions/TransactionCategoriseModal';
import { TransactionSplitModal } from '../components/transactions/TransactionSplitModal';
import { TransactionTagModal } from '../components/transactions/TransactionTagModal';
import { DeleteTransactionModal } from '../components/transactions/DeleteTransactionModal';
import { BulkDeleteTransactionModal } from '../components/transactions/BulkDeleteTransactionModal';
import { ImportSessionHistory } from '../components/transactions/ImportSessionHistory';
import { TransactionErrorBoundary } from '../components/transactions/TransactionErrorBoundary';
import { useTransactions, useDeleteTransaction, useBulkDeleteTransactions } from '../hooks/useTransactions';
import { getCurrentUserId } from '../lib/supabase';
import { useQuery } from '@tanstack/react-query';
import { transactionService } from '../services/transaction-service';
import { Upload, AlertCircle } from 'lucide-react';
import type { Transaction } from '../types/transaction.types';
import { PageHeader } from '../components/PageHeader';

function useTransactionStatusCounts(
  userId?: string | null,
  filters?: {
    accountId?: string;
    categoryIds?: string[];
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    type?: string;
  }
) {
  return useQuery({
    queryKey: ['transactionStatusCounts', userId, filters],
    enabled: !!userId,
    queryFn: async () => {
      // Build base query params with current filters (excluding status)
      const baseParams: any = {
        userId: userId!,
        limit: 1,
        page: 1
      };

      if (filters?.accountId) {
        baseParams.accountId = filters.accountId;
      }
      if (filters?.categoryIds && filters.categoryIds.length > 0) {
        baseParams.categoryIds = filters.categoryIds;
      }
      if (filters?.dateFrom) {
        baseParams.dateFrom = filters.dateFrom;
      }
      if (filters?.dateTo) {
        baseParams.dateTo = filters.dateTo;
      }
      if (filters?.search) {
        baseParams.search = filters.search;
      }
      if (filters?.type && filters.type !== 'all') {
        baseParams.type = filters.type;
      }

      // Get pending count with filters applied
      const result = await transactionService.listTransactions({
        ...baseParams,
        status: 'pending'
      });
      const pendingCount = result.data?.total ?? 0;

      // Get needs_review count with filters applied
      const reviewResult = await transactionService.listTransactions({
        ...baseParams,
        status: 'needs_review'
      });
      const reviewCount = reviewResult.data?.total ?? 0;

      return { pendingCount, reviewCount };
    },
    staleTime: 60 * 1000,
  });
}

export function TransactionsPage(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCategoriseModal, setShowCategoriseModal] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [bulkDeleteTransactionIds, setBulkDeleteTransactionIds] = useState<string[]>([]);
  const [filterAccount, setFilterAccount] = useState<string | undefined>(() => {
    return searchParams.get('accountId') || undefined;
  });
  const [filterAccountIds, setFilterAccountIds] = useState<string[]>(() => {
    const accountIdsParam = searchParams.get('accountIds');
    if (!accountIdsParam) return [];
    return accountIdsParam
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  });
  const [userId, setUserId] = useState<string | null>(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<number>(() => {
    const limitParam = searchParams.get('limit');
    if (limitParam) {
      const parsed = parseInt(limitParam, 10);
      return isNaN(parsed) ? 50 : parsed;
    }
    return 50;
  });
  
  // Filter state - initialize from URL params
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>(() => {
    const statusParam = searchParams.get('status');
    return (statusParam === 'pending' || statusParam === 'needs_review' || statusParam === 'completed') ? statusParam : 'all';
  });
  const [filterCategory, setFilterCategory] = useState<string[]>(() => {
    const categoriesParam = searchParams.get('categories');
    const legacyCategoryParam = searchParams.get('category');
    const raw = categoriesParam ?? legacyCategoryParam ?? '';
    if (!raw) {
      return [];
    }
    return raw
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  });
  // Initialize dates from URL or default to last month
  const getDefaultDateRange = () => {
    const now = new Date();
    const lastMonth = subMonths(now, 1);
    return {
      from: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
      to: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
    };
  };

  const [dateFrom, setDateFrom] = useState<string>(() => {
    const urlDateFrom = searchParams.get('dateFrom');
    if (urlDateFrom) return urlDateFrom;
    return getDefaultDateRange().from;
  });
  const [dateTo, setDateTo] = useState<string>(() => {
    const urlDateTo = searchParams.get('dateTo');
    if (urlDateTo) return urlDateTo;
    return getDefaultDateRange().to;
  });
  
  // Sort state
  const [sortField, setSortField] = useState<'date' | 'category' | 'amount'>(() => {
    const sortFieldParam = searchParams.get('sortField');
    return (sortFieldParam === 'category' || sortFieldParam === 'amount') ? sortFieldParam : 'date';
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    const sortParam = searchParams.get('sort');
    return sortParam === 'asc' ? 'asc' : 'desc';
  });

  // Update filters when URL params change
  useEffect(() => {
    const parseCategoryValues = (raw?: string | null) =>
      raw
        ? raw
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
        : [];

    const categoriesParam = searchParams.get('categories');
    const legacyCategoryParam = searchParams.get('category');
    const nextCategories = parseCategoryValues(categoriesParam ?? legacyCategoryParam);

    setFilterCategory((prev) => {
      const sameLength = prev.length === nextCategories.length;
      const sameValues = sameLength && prev.every((id, index) => id === nextCategories[index]);
      if (sameValues) {
        return prev;
      }
      return nextCategories;
    });

    const dateFromParam = searchParams.get('dateFrom');
    const dateToParam = searchParams.get('dateTo');

    if (dateFromParam !== null) {
      setDateFrom(dateFromParam);
    } else {
      // Reset to empty if not in URL
      setDateFrom('');
    }
    if (dateToParam !== null) {
      setDateTo(dateToParam);
    } else {
      // Reset to empty if not in URL
      setDateTo('');
    }

    // Update limit from URL params
    const limitParam = searchParams.get('limit');
    if (limitParam) {
      const parsed = parseInt(limitParam, 10);
      if (!isNaN(parsed)) {
        setLimit((prev) => prev !== parsed ? parsed : prev);
      }
    } else {
      // Reset to default if no limit param
      setLimit((prev) => prev !== 50 ? 50 : prev);
    }

    // Update sort field and order from URL params
    const sortFieldParam = searchParams.get('sortField');
    if (sortFieldParam === 'category' || sortFieldParam === 'amount') {
      setSortField((prev) => prev !== sortFieldParam ? sortFieldParam : prev);
    } else {
      setSortField((prev) => prev !== 'date' ? 'date' : prev);
    }
    const sortParam = searchParams.get('sort');
    if (sortParam === 'asc' || sortParam === 'desc') {
      setSortOrder((prev) => prev !== sortParam ? sortParam : prev);
    } else {
      // Reset to default if no sort param
      setSortOrder((prev) => prev !== 'desc' ? 'desc' : prev);
    }

    // Update account filter from URL params
    const accountIdParam = searchParams.get('accountId');
    if (accountIdParam !== null) {
      setFilterAccount((prev) => prev !== accountIdParam ? accountIdParam : prev);
    } else {
      // Reset to undefined if no accountId param
      setFilterAccount((prev) => prev !== undefined ? undefined : prev);
    }

    // Update accountIds filter from URL params
    const accountIdsParam = searchParams.get('accountIds');
    if (accountIdsParam !== null) {
      const nextAccountIds = accountIdsParam
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      setFilterAccountIds((prev) => {
        const sameLength = prev.length === nextAccountIds.length;
        const sameValues = sameLength && prev.every((id, index) => id === nextAccountIds[index]);
        if (sameValues) {
          return prev;
        }
        return nextAccountIds;
      });
    } else {
      // Reset to empty if no accountIds param
      setFilterAccountIds((prev) => prev.length > 0 ? [] : prev);
    }

    // Update status filter from URL params
    const statusParam = searchParams.get('status');
    if (statusParam === 'pending' || statusParam === 'needs_review' || statusParam === 'completed') {
      setFilterStatus((prev) => prev !== statusParam ? statusParam : prev);
    } else {
      // Reset to 'all' if no status param or invalid value
      setFilterStatus((prev) => prev !== 'all' ? 'all' : prev);
    }
  }, [searchParams]);

  // Get current user ID
  useEffect(() => {
    getCurrentUserId().then(id => setUserId(id));
  }, []);

  // Track if we've initialized dates on mount (only run once)
  const hasInitializedDates = React.useRef(false);
  
  // Initialize date range in URL if not present (only on initial mount)
  useEffect(() => {
    // Only initialize on first mount, not on every searchParams change
    if (hasInitializedDates.current) {
      return;
    }
    
    const dateFromParam = searchParams.get('dateFrom');
    const dateToParam = searchParams.get('dateTo');
    
    // If no dates in URL on initial mount, set default (last month) and update URL
    if (!dateFromParam && !dateToParam) {
      const defaultRange = getDefaultDateRange();
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('dateFrom', defaultRange.from);
      newSearchParams.set('dateTo', defaultRange.to);
      setSearchParams(newSearchParams, { replace: true });
      hasInitializedDates.current = true;
      // State will be updated by the useEffect that watches searchParams
    } else {
      // Dates exist in URL, mark as initialized
      hasInitializedDates.current = true;
    }
  }, [searchParams, setSearchParams]);

  // Debounce search input to reduce rapid refetches
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === '') {
      setSearchQuery('');
      return;
    }
    const handler = setTimeout(() => {
      setSearchQuery(trimmed);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterType, filterStatus, filterCategory, dateFrom, dateTo, filterAccount, filterAccountIds, limit, sortField, sortOrder]);

  // Helper function to preserve all current filter values when updating URL params
  const updateSearchParamsWithAllFilters = (updater: (params: URLSearchParams) => void) => {
    // Start with current URL params to preserve everything
    const newSearchParams = new URLSearchParams(searchParams);
    
    // Update with current state values (state is source of truth)
    // BUT: If state is empty and URL has dates, preserve URL dates (user might have "All dates" selected)
    // Only delete dates if state is empty AND we're explicitly clearing them (not just updating limit)
    const urlHasDates = searchParams.get('dateFrom') || searchParams.get('dateTo');
    if (dateFrom) {
      newSearchParams.set('dateFrom', dateFrom);
    } else if (urlHasDates) {
      // Preserve existing URL dates when state is empty (user has "All dates" but URL still has dates from previous selection)
      // Don't delete - let them stay in URL to prevent initialization useEffect from firing
    } else {
      newSearchParams.delete('dateFrom');
    }
    if (dateTo) {
      newSearchParams.set('dateTo', dateTo);
    } else if (urlHasDates) {
      // Preserve existing URL dates when state is empty
    } else {
      newSearchParams.delete('dateTo');
    }
    // accountIds takes precedence over accountId if both are set
    if (filterAccountIds.length > 0) {
      newSearchParams.set('accountIds', filterAccountIds.join(','));
      newSearchParams.delete('accountId');
    } else if (filterAccount) {
      newSearchParams.set('accountId', filterAccount);
      newSearchParams.delete('accountIds');
    } else {
      newSearchParams.delete('accountId');
      newSearchParams.delete('accountIds');
    }
    if (filterCategory.length > 0) {
      newSearchParams.set('categories', filterCategory.join(','));
    } else {
      newSearchParams.delete('categories');
    }
    if (filterType !== 'all') {
      newSearchParams.set('type', filterType);
    } else {
      newSearchParams.delete('type');
    }
    if (filterStatus !== 'all') {
      newSearchParams.set('status', filterStatus);
    } else {
      newSearchParams.delete('status');
    }
    if (searchQuery) {
      newSearchParams.set('search', searchQuery);
    } else {
      newSearchParams.delete('search');
    }
    newSearchParams.set('sortField', sortField);
    newSearchParams.set('sort', sortOrder);
    newSearchParams.set('limit', limit.toString());
    
    // Apply the specific update (this may override or delete specific params)
    updater(newSearchParams);
    return newSearchParams;
  };

  // Handle sort change
  const handleSortChange = (field: 'date' | 'category' | 'amount', order: 'asc' | 'desc') => {
    setSortField(field);
    setSortOrder(order);
    setPage(1); // Reset to first page
    // Update URL params - preserve all other filters
    const newSearchParams = updateSearchParamsWithAllFilters((params) => {
      params.set('sortField', field);
      params.set('sort', order);
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  // Handle limit change
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page
    // Update URL param - preserve all other filters
    // For limit changes, preserve existing URL params BUT:
    // - If state is empty (user has "All dates"), delete dates from URL to keep it cleared
    // - If state has dates, preserve them
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('limit', newLimit.toString());
    
    // If state is empty (user wants "All dates"), remove dates from URL
    if (!dateFrom && !dateTo) {
      newSearchParams.delete('dateFrom');
      newSearchParams.delete('dateTo');
    }
    
    setSearchParams(newSearchParams, { replace: true });
  };

  // Handle account filter change
  const handleAccountFilterChange = (accountId: string | undefined) => {
    setFilterAccount(accountId);
    setPage(1); // Reset to first page
    // Update URL param - preserve all current filter values
    const newSearchParams = updateSearchParamsWithAllFilters((params) => {
      if (accountId) {
        params.set('accountId', accountId);
      } else {
        params.delete('accountId');
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  // Handle date range change
  const handleDateFromChange = (newDateFrom: string) => {
    setDateFrom(newDateFrom);
    setPage(1); // Reset to first page
    // Update URL param - preserve all other filters
    const newSearchParams = updateSearchParamsWithAllFilters((params) => {
      if (newDateFrom) {
        params.set('dateFrom', newDateFrom);
      } else {
        params.delete('dateFrom');
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleDateToChange = (newDateTo: string) => {
    setDateTo(newDateTo);
    setPage(1); // Reset to first page
    // Update URL param - preserve all other filters
    const newSearchParams = updateSearchParamsWithAllFilters((params) => {
      if (newDateTo) {
        params.set('dateTo', newDateTo);
      } else {
        params.delete('dateTo');
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  // Handle date range change (both dates at once) - used by DateRangeSelector
  const handleDateRangeChange = (range: { from: string; to: string }) => {
    // Update both dates in a single state update to avoid race conditions
    setDateFrom(range.from);
    setDateTo(range.to);
    setPage(1); // Reset to first page
    // Update URL param - preserve all other filters
    const newSearchParams = updateSearchParamsWithAllFilters((params) => {
      if (range.from) {
        params.set('dateFrom', range.from);
      } else {
        params.delete('dateFrom');
      }
      if (range.to) {
        params.set('dateTo', range.to);
      } else {
        params.delete('dateTo');
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  // Handle filter type change
  const handleFilterTypeChange = (newFilterType: string) => {
    setFilterType(newFilterType);
    setPage(1); // Reset to first page
    // Update URL param - preserve all other filters
    const newSearchParams = updateSearchParamsWithAllFilters((params) => {
      if (newFilterType !== 'all') {
        params.set('type', newFilterType);
      } else {
        params.delete('type');
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  // Handle filter status change
  const handleFilterStatusChange = (newFilterStatus: string) => {
    setFilterStatus(newFilterStatus);
    setPage(1); // Reset to first page
    // Update URL param - preserve all other filters
    const newSearchParams = updateSearchParamsWithAllFilters((params) => {
      if (newFilterStatus !== 'all') {
        params.set('status', newFilterStatus);
      } else {
        params.delete('status');
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  // Handle filter category change
  const handleFilterCategoryChange = (newFilterCategory: string[]) => {
    setFilterCategory(newFilterCategory);
    setPage(1); // Reset to first page
    // Update URL param - preserve all other filters
    const newSearchParams = updateSearchParamsWithAllFilters((params) => {
      if (newFilterCategory.length > 0) {
        params.set('categories', newFilterCategory.join(','));
      } else {
        params.delete('categories');
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  // Fetch transactions using TanStack Query
  // Only use server-side sorting for date field; category and amount are sorted client-side
  const queryParams = {
    userId: userId || '',
    // accountIds takes precedence over accountId if both are set
    accountIds: filterAccountIds.length > 0 ? filterAccountIds : undefined,
    accountId: filterAccountIds.length === 0 ? filterAccount : undefined,
    categoryIds: filterCategory.length > 0 ? filterCategory : undefined,
    status: filterStatus !== 'all' ? filterStatus as 'completed' | 'pending' | 'needs_review' : undefined,
    type: filterType !== 'all' ? filterType as 'debit' | 'credit' | 'transfer' : undefined,
    search: searchQuery || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    page,
    limit,
    sortOrder: sortField === 'date' ? sortOrder : 'desc' // Only use server-side sort for date
  };
  const { data: transactionsData, isLoading, error: queryError, refetch } = useTransactions(queryParams);

  const transactions = transactionsData?.transactions || [];
  const total = transactionsData?.total || 0;
  const totalPages = Math.ceil(total / limit);
  const loading = isLoading;
  const error = queryError ? (queryError as Error).message : null;

  const { data: statusCounts } = useTransactionStatusCounts(userId, {
    // For status counts, use accountId if single, otherwise we'd need to update the hook
    // For now, status counts will only work with single account filter
    accountId: filterAccountIds.length === 0 ? filterAccount : undefined,
    categoryIds: filterCategory.length > 0 ? filterCategory : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    search: searchQuery || undefined,
    type: filterType !== 'all' ? filterType : undefined
  });
  const pendingCount = statusCounts?.pendingCount ?? 0;
  const reviewCount = statusCounts?.reviewCount ?? 0;

  const hasActiveFilters =
    !!(
      searchInput.trim() ||
      (filterType && filterType !== 'all') ||
      (filterStatus && filterStatus !== 'all') ||
      filterCategory.length > 0 ||
      filterAccount ||
      filterAccountIds.length > 0 ||
      dateFrom ||
      dateTo
    );

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setFilterType('all');
    setFilterStatus('all');
    setFilterCategory([]);
    setFilterAccount(undefined);
    setFilterAccountIds([]);
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  // Ensure current page stays in range after data changes (e.g. updates affecting filters)
  useEffect(() => {
    if (loading) {
      return;
    }

    const calculatedTotalPages = total > 0 ? Math.ceil(total / limit) : 0;

    // If there are no results but we're on a page > 1, step back one page
    if (transactions.length === 0 && total > 0 && page > 1) {
      setPage((prev) => Math.max(1, prev - 1));
      return;
    }

    // If total pages shrink below current page, jump to the last page
    if (calculatedTotalPages > 0 && page > calculatedTotalPages) {
      setPage(calculatedTotalPages);
      return;
    }

    // If there are no results at all, ensure we reset to page 1
    if (total === 0 && page !== 1) {
      setPage(1);
    }
  }, [loading, total, transactions.length, page, limit]);

  // Mutations
  const deleteTransactionMutation = useDeleteTransaction();
  const bulkDeleteMutation = useBulkDeleteTransactions();

  const handleImportComplete = () => {
    refetch();
  };

  const handleEdit = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowEditModal(true);
  };

  const handleCategorise = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowCategoriseModal(true);
  };

  const handleSplit = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowSplitModal(true);
  };

  const handleTag = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowTagModal(true);
  };

  const handleDelete = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTransaction) return;

    try {
      await deleteTransactionMutation.mutateAsync(selectedTransaction.id);
      setShowDeleteModal(false);
      setSelectedTransaction(null);
    } catch (err) {
      console.error('Failed to delete transaction:', err);
      // Keep modal open on error so user can try again
    }
  };

  const handleBulkAction = async (action: string, transactionIds: string[]) => {
    try {
      switch (action) {
        case 'categorise':
          // Handled inline in TransactionList component
          break;
        case 'tag':
          // TODO: Open bulk tag modal
          break;
        case 'delete':
          setBulkDeleteTransactionIds(transactionIds);
          setShowBulkDeleteModal(true);
          break;
      }
    } catch (err) {
      console.error('Failed to perform bulk action:', err);
    }
  };

  const handleConfirmBulkDelete = async () => {
    if (bulkDeleteTransactionIds.length === 0) return;

    try {
      await bulkDeleteMutation.mutateAsync(bulkDeleteTransactionIds);
      setShowBulkDeleteModal(false);
      setBulkDeleteTransactionIds([]);
    } catch (err) {
      console.error('Failed to delete transactions:', err);
      // Keep modal open on error so user can try again
    }
  };


  return (
    <TransactionErrorBoundary>
      <div className="space-y-6">
        {/* Page Header */}
        <PageHeader
          title="Transactions"
          description="View and manage your financial transactions"
          helpSlug="transactions"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                {(pendingCount > 0 || reviewCount > 0) && (
                  <div className="flex items-center space-x-4">
                    {pendingCount > 0 && (
                      <button
                        onClick={() => handleFilterStatusChange('pending')}
                        className={`flex items-center space-x-2 px-3 py-1.5 border rounded-lg transition-all duration-200 ${
                          filterStatus === 'pending'
                            ? 'bg-amber-100 dark:bg-amber-900/40 border-amber-300 dark:border-amber-700 shadow-sm'
                            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30 hover:border-amber-300 dark:hover:border-amber-700'
                        } cursor-pointer`}
                        aria-label={`Filter to show ${pendingCount} uncategorised transactions`}
                      >
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-semibold">
                          {pendingCount}
                        </span>
                        <span className="text-sm font-medium text-amber-700 dark:text-amber-200">
                          Uncategorised
                        </span>
                      </button>
                    )}
                    {reviewCount > 0 && (
                      <div className="flex items-center space-x-2 px-3 py-1.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white text-xs font-semibold">
                          {reviewCount}
                        </span>
                        <span className="text-sm font-medium text-red-700 dark:text-red-200">
                          Need Review
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => setShowImportModal(true)}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center space-x-2 w-full sm:w-auto"
                  aria-label="Import transactions"
                >
                  <Upload className="h-5 w-5" />
                  <span>Import Transactions</span>
                </button>
          </div>
        </PageHeader>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4" role="alert">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                <span className="text-red-800 dark:text-red-200 font-medium">{error}</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => refetch()}
                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 text-sm font-medium"
                  aria-label="Retry"
                >
                  Retry
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                  aria-label="Dismiss error"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Transaction List */}
        {loading && transactions.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12">
            <div className="flex items-center justify-center">
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
                <p className="text-lg text-gray-600 dark:text-gray-400 font-medium">Loading transactions...</p>
              </div>
            </div>
          </div>
        ) : (
          <TransactionList
            transactions={transactions}
            loading={loading}
            onEdit={handleEdit}
            onCategorise={handleCategorise}
            onSplit={handleSplit}
            onTag={handleTag}
            onDelete={handleDelete}
            onBulkAction={handleBulkAction}
            // Pagination props
            total={total}
            page={page}
            limit={limit}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
            // Filter props
            searchQuery={searchInput}
            onSearchChange={setSearchInput}
            filterType={filterType}
            onFilterTypeChange={handleFilterTypeChange}
            filterStatus={filterStatus}
            onFilterStatusChange={handleFilterStatusChange}
            filterCategory={filterCategory}
            onFilterCategoryChange={handleFilterCategoryChange}
            filterAccount={filterAccount}
            filterAccountIds={filterAccountIds}
            onFilterAccountChange={handleAccountFilterChange}
            onFilterAccountIdsChange={(accountIds) => {
              setFilterAccountIds(accountIds);
              setFilterAccount(undefined); // Clear single account filter when using multiple
              setPage(1);
              const newSearchParams = updateSearchParamsWithAllFilters((params) => {
                if (accountIds.length > 0) {
                  params.set('accountIds', accountIds.join(','));
                  params.delete('accountId');
                } else {
                  params.delete('accountIds');
                }
              });
              setSearchParams(newSearchParams, { replace: true });
            }}
            dateFrom={dateFrom}
            onDateFromChange={handleDateFromChange}
            dateTo={dateTo}
            onDateToChange={handleDateToChange}
            onDateRangeChange={handleDateRangeChange}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={hasActiveFilters ? handleClearFilters : undefined}
            sortField={sortField}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          />
        )}

        {/* Import Session History */}
        <div>
          <ImportSessionHistory />
        </div>

        {/* Import Modal */}
        {showImportModal && (
          <ImportModal
            isOpen={showImportModal}
            onClose={() => setShowImportModal(false)}
            accountId={filterAccount}
            onImportComplete={handleImportComplete}
          />
        )}

        {/* Edit Modal */}
        {showEditModal && selectedTransaction && (
          <TransactionEditModal
            transaction={selectedTransaction}
            onClose={() => {
              setShowEditModal(false);
              setSelectedTransaction(null);
            }}
            onSave={() => {
              setShowEditModal(false);
              setSelectedTransaction(null);
              refetch();
            }}
          />
        )}

        {/* Categorise Modal */}
        {showCategoriseModal && selectedTransaction && (
          <TransactionCategoriseModal
            isOpen={showCategoriseModal}
            transaction={selectedTransaction}
            onClose={() => {
              setShowCategoriseModal(false);
              setSelectedTransaction(null);
            }}
            onComplete={() => {
              refetch();
            }}
          />
        )}

        {/* Split Modal */}
        {showSplitModal && selectedTransaction && (
          <TransactionSplitModal
            transaction={selectedTransaction}
            onClose={() => {
              setShowSplitModal(false);
              setSelectedTransaction(null);
            }}
            onSave={() => {
              setShowSplitModal(false);
              setSelectedTransaction(null);
              refetch();
            }}
          />
        )}

        {/* Tag Modal */}
        {showTagModal && selectedTransaction && (
          <TransactionTagModal
            transaction={selectedTransaction}
            onClose={() => {
              setShowTagModal(false);
              setSelectedTransaction(null);
            }}
            onSave={() => {
              setShowTagModal(false);
              setSelectedTransaction(null);
              refetch();
            }}
          />
        )}

        {/* Delete Modal */}
        <DeleteTransactionModal
          isOpen={showDeleteModal}
          transaction={selectedTransaction}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedTransaction(null);
          }}
          onConfirm={handleConfirmDelete}
          loading={deleteTransactionMutation.isPending}
        />

        {/* Bulk Delete Modal */}
        <BulkDeleteTransactionModal
          isOpen={showBulkDeleteModal}
          transactionCount={bulkDeleteTransactionIds.length}
          onClose={() => {
            setShowBulkDeleteModal(false);
            setBulkDeleteTransactionIds([]);
          }}
          onConfirm={handleConfirmBulkDelete}
          loading={bulkDeleteMutation.isPending}
        />
      </div>
    </TransactionErrorBoundary>
  );
}
